import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import services from "../../services";
import ownerAPI from "../../services/ownerApi";
import { getDashboardPath } from "../../utils/authRedirect";
import { getSubdomainForForms } from "../../utils/portalHost";
import {
  getStoredSubdomain,
  setStoredSubdomain,
} from "../../utils/clinicStorage";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
} from "../../utils/authStorage";
import { getApiErrorMessage } from "../../utils/apiError";
import { getAdAttributionPayload } from "../../utils/adAttribution";
import { trackRegistrationSuccess } from "../../analytics/metrikaGoals";

export { getStoredSubdomain, setStoredSubdomain };

const applyAuthPayload = (state, payload) => {
  if (payload?.result !== 0) return;
  if (payload.registration_pending) {
    state.isAuth = false;
    state.isPlatformAdmin = false;
    state.registrationPending = true;
    state.profile = payload.user ?? null;
    state.tenant = payload.tenant ?? state.tenant;
    return;
  }
  state.registrationPending = false;
  if (payload.accessToken) setAccessToken(payload.accessToken);
  state.isPlatformAdmin = false;
  state.isAuth = true;
  state.myId = payload.user?.id ?? null;
  state.profile = payload.user ?? null;
  state.role = payload.user?.role ?? null;
  state.tenant = payload.tenant ?? state.tenant;
  state.club = payload.club ?? state.club;
  state.trainerProfile =
    payload.trainerProfile ?? payload.doctorProfile ?? state.trainerProfile;
  state.billing = payload.billing ?? null;
  state.entitlements = payload.entitlements ?? state.entitlements ?? null;
};

export const fetchAuth = createAsyncThunk(
  "auth/fetch",
  async ({ subdomain, login, password, role }, { rejectWithValue }) => {
    try {
      const normalized = (subdomain || getSubdomainForForms() || "")
        .trim()
        .toLowerCase();
      if (!normalized) {
        return rejectWithValue("Укажите код клуба");
      }
      setStoredSubdomain(normalized);
      return await services.authAPI.logIn(normalized, login, password, role);
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, "Ошибка входа"));
    }
  },
);

const applyPlatformAuthPayload = (state, payload) => {
  if (payload?.result !== 0) return;
  state.registrationPending = false;
  if (payload.accessToken) setAccessToken(payload.accessToken);
  state.isAuth = true;
  state.isPlatformAdmin = true;
  state.myId = null;
  state.profile = payload.admin ?? null;
  state.role = "platform_admin";
  state.tenant = null;
  state.club = null;
  state.trainerProfile = null;
  state.billing = null;
  state.entitlements = null;
};

export const fetchOwnerAuth = createAsyncThunk(
  "auth/fetchOwner",
  async ({ login, password }, { rejectWithValue }) => {
    try {
      return await services.authAPI.logInOwner(login, password);
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, "Ошибка входа"));
    }
  },
);

export const fetchPlatformAuth = createAsyncThunk(
  "auth/fetchPlatform",
  async ({ login, password }, { rejectWithValue }) => {
    try {
      return await services.authAPI.logInPlatformAdmin(login, password);
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, "Ошибка входа"));
    }
  },
);

export const fetchPlatformMe = createAsyncThunk(
  "authPlatformMe/fetch",
  async (_, { rejectWithValue }) => {
    try {
      return await services.authAPI.platformMe();
    } catch (e) {
      return rejectWithValue({
        message: getApiErrorMessage(e, "Не удалось получить профиль"),
        status: e.response?.status,
      });
    }
  },
);

export const fetchPlatformLogout = createAsyncThunk("platformLogout", async () => {
  try {
    return await services.authAPI.platformLogOut();
  } catch (_) {
    return { result: 0 };
  }
});

export const fetchAuthMe = createAsyncThunk(
  "authMe/fetch",
  async (_, { rejectWithValue }) => {
    try {
      return await services.authAPI.me();
    } catch (e) {
      return rejectWithValue({
        message: getApiErrorMessage(e, "Не удалось получить профиль"),
        status: e.response?.status,
      });
    }
  },
);

export const fetchReg = createAsyncThunk(
  "reg/fetch",
  async (form, { rejectWithValue }) => {
    try {
      const payload = {
        clinicName: form.clinicName?.trim(),
        subdomain: form.subdomain?.trim().toLowerCase(),
        email: form.login?.trim(),
        password: form.password,
        fullName: form.fullName?.trim(),
        phone: form.phone?.trim() || undefined,
        ...getAdAttributionPayload(),
      };
      const result = await services.authAPI.register(payload);
      setStoredSubdomain(payload.subdomain);
      if (result?.result === 0) {
        trackRegistrationSuccess();
      }
      return result;
    } catch (e) {
      return rejectWithValue({
        message: getApiErrorMessage(e, "Ошибка регистрации"),
        code: e.response?.data?.code || null,
      });
    }
  },
);

export const fetchCreateOwnerClub = createAsyncThunk(
  "reg/createOwnerClub",
  async (form, { rejectWithValue }) => {
    try {
      const payload = {
        clinicName: form.clinicName?.trim(),
        subdomain: form.subdomain?.trim().toLowerCase(),
        phone: form.phone?.trim() || undefined,
        ...getAdAttributionPayload(),
      };
      const result = await ownerAPI.createClub(payload);
      setStoredSubdomain(payload.subdomain);
      if (result?.result === 0) {
        trackRegistrationSuccess();
      }
      return result;
    } catch (e) {
      return rejectWithValue({
        message: getApiErrorMessage(e, "Не удалось создать клуб"),
        code: e.response?.data?.code || null,
      });
    }
  },
);

export const fetchRegPatient = createAsyncThunk(
  "regPatient/fetch",
  async (form, { rejectWithValue }) => {
    try {
      const sub = (
        form.subdomain ||
        getSubdomainForForms() ||
        ""
      )
        .trim()
        .toLowerCase();
      const payload = {
        subdomain: sub,
        email: form.login?.trim() || undefined,
        password: form.password,
        fullName: form.fullName?.trim(),
        phone: form.phone?.trim(),
      };
      if (!payload.phone) return rejectWithValue("Укажите телефон");
      if (!sub) return rejectWithValue("Укажите код клуба");
      setStoredSubdomain(sub);
      return await services.authAPI.registerClient(payload);
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, "Ошибка регистрации"));
    }
  },
);

export const fetchTenantBilling = createAsyncThunk(
  "auth/fetchBilling",
  async (_, { rejectWithValue }) => {
    try {
      const r = await services.clinicAPI.getTenantBilling();
      return r.billing ?? null;
    } catch (e) {
      return rejectWithValue(getApiErrorMessage(e, "Не удалось загрузить подписку"));
    }
  },
);

export const logoutFetch = createAsyncThunk("logout", async () => {
  try {
    return await services.authAPI.logOut();
  } catch (_) {
    return { result: 0 };
  }
});

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    isAuth: false,
    isPlatformAdmin: false,
    registrationPending: false,
    load: false,
    myId: null,
    err: "",
    role: null,
    profile: null,
    tenant: null,
    club: null,
    trainerProfile: null,
    billing: null,
    billingLoad: false,
    entitlements: null,
  },
  reducers: {
    clearErr(state) {
      state.err = "";
      state.registrationPending = false;
    },
    resetLoad(state) {
      state.load = false;
    },
    sessionExpired(state) {
      clearAccessToken();
      state.isAuth = false;
      state.isPlatformAdmin = false;
      state.profile = null;
      state.myId = null;
      state.role = null;
      state.tenant = null;
      state.club = null;
      state.trainerProfile = null;
      state.billing = null;
      state.entitlements = null;
    },
    updateTenant(state, action) {
      if (state.tenant && action.payload) {
        state.tenant = { ...state.tenant, ...action.payload };
      }
    },
    setBilling(state, action) {
      state.billing = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    const pending = (state) => {
      state.load = true;
      state.err = "";
    };
    const rejected = (state, action) => {
      const payload = action.payload;
      state.err =
        typeof payload === "object" && payload?.message
          ? payload
          : payload || action.error.message;
      state.load = false;
    };

    builder.addCase(fetchAuth.fulfilled, (state, action) => {
      applyAuthPayload(state, action.payload);
      if (action.payload?.result !== 0) {
        state.err = action.payload?.error || "Неверные учетные данные";
      }
      state.load = false;
    });
    builder.addCase(fetchAuth.pending, pending);
    builder.addCase(fetchAuth.rejected, rejected);

    builder.addCase(fetchOwnerAuth.fulfilled, (state, action) => {
      applyAuthPayload(state, action.payload);
      if (action.payload?.result !== 0) {
        state.err = action.payload?.error || "Неверные учетные данные";
      }
      state.load = false;
    });
    builder.addCase(fetchOwnerAuth.pending, pending);
    builder.addCase(fetchOwnerAuth.rejected, rejected);

    builder.addCase(fetchPlatformAuth.fulfilled, (state, action) => {
      applyPlatformAuthPayload(state, action.payload);
      if (action.payload?.result !== 0) {
        state.err = action.payload?.error || "Неверные учетные данные";
      }
      state.load = false;
    });
    builder.addCase(fetchPlatformAuth.pending, pending);
    builder.addCase(fetchPlatformAuth.rejected, rejected);

    builder.addCase(fetchPlatformMe.fulfilled, (state, action) => {
      if (action.payload?.result === 0 && action.payload.admin) {
        state.isAuth = true;
        state.isPlatformAdmin = true;
        state.profile = action.payload.admin;
        state.role = "platform_admin";
        state.tenant = null;
        state.club = null;
        state.trainerProfile = null;
        state.billing = null;
      } else {
        clearAccessToken();
        state.isAuth = false;
        state.isPlatformAdmin = false;
      }
      state.load = false;
    });
    builder.addCase(fetchPlatformMe.rejected, (state, action) => {
      const status = action.payload?.status;
      if (status === 401 || status === 403) {
        clearAccessToken();
        state.isAuth = false;
        state.isPlatformAdmin = false;
      }
      state.load = false;
    });

    const clearPlatformSession = (state) => {
      clearAccessToken();
      state.isAuth = false;
      state.isPlatformAdmin = false;
      state.profile = null;
      state.role = null;
    };
    builder.addCase(fetchPlatformLogout.pending, clearPlatformSession);
    builder.addCase(fetchPlatformLogout.fulfilled, clearPlatformSession);
    builder.addCase(fetchPlatformLogout.rejected, clearPlatformSession);

    builder.addCase(fetchReg.fulfilled, (state, action) => {
      applyAuthPayload(state, action.payload);
      if (action.payload?.result !== 0) {
        state.err = action.payload?.error || "Ошибка регистрации";
      }
      state.load = false;
    });
    builder.addCase(fetchReg.pending, pending);
    builder.addCase(fetchReg.rejected, rejected);

    builder.addCase(fetchCreateOwnerClub.fulfilled, (state, action) => {
      applyAuthPayload(state, action.payload);
      if (action.payload?.result !== 0) {
        state.err = action.payload?.error || "Не удалось создать клуб";
      }
      state.load = false;
    });
    builder.addCase(fetchCreateOwnerClub.pending, pending);
    builder.addCase(fetchCreateOwnerClub.rejected, rejected);

    builder.addCase(fetchRegPatient.fulfilled, (state, action) => {
      applyAuthPayload(state, action.payload);
      if (action.payload?.result !== 0) {
        state.err = action.payload?.error || "Ошибка регистрации";
      }
      state.load = false;
    });
    builder.addCase(fetchRegPatient.pending, pending);
    builder.addCase(fetchRegPatient.rejected, rejected);

    builder.addCase(fetchAuthMe.fulfilled, (state, action) => {
      if (action.payload?.result === 0 && action.payload.user) {
        state.myId = action.payload.user.id;
        state.isAuth = true;
        state.isPlatformAdmin = false;
        state.profile = action.payload.user;
        state.role = action.payload.user.role;
        state.tenant = action.payload.tenant
          ? { ...(state.tenant || {}), ...action.payload.tenant }
          : state.tenant;
        state.club = action.payload.club || null;
        state.trainerProfile =
          action.payload.trainerProfile || action.payload.doctorProfile || null;
        state.billing = action.payload.billing ?? null;
        state.entitlements = action.payload.entitlements ?? null;
      } else {
        clearAccessToken();
        state.isAuth = false;
      }
      state.load = false;
    });
    builder.addCase(fetchAuthMe.rejected, (state, action) => {
      const status = action.payload?.status;
      if (status === 401 || status === 403) {
        clearAccessToken();
        state.isAuth = false;
      }
      state.load = false;
    });

    builder.addCase(fetchTenantBilling.pending, (state) => {
      state.billingLoad = true;
    });
    builder.addCase(fetchTenantBilling.fulfilled, (state, action) => {
      state.billing = action.payload ?? null;
      state.billingLoad = false;
    });
    builder.addCase(fetchTenantBilling.rejected, (state) => {
      state.billingLoad = false;
    });

    const clearSession = (state) => {
      clearAccessToken();
      state.isAuth = false;
      state.isPlatformAdmin = false;
      state.profile = null;
      state.myId = null;
      state.role = null;
      state.tenant = null;
      state.club = null;
      state.trainerProfile = null;
      state.billing = null;
      state.billingLoad = false;
      state.entitlements = null;
    };
    builder.addCase(logoutFetch.pending, clearSession);
    builder.addCase(logoutFetch.fulfilled, clearSession);
    builder.addCase(logoutFetch.rejected, clearSession);
  },
});

export const { clearErr, resetLoad, sessionExpired, updateTenant, setBilling } =
  authSlice.actions;
export default authSlice.reducer;
